import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoginRequest } from '../../../pages/login/models/login-request.model';
import { LoginResponse } from '../../../pages/login/models/login-response.model';
import { ApiResponse } from '../../models/api-response.model';
import { VerifySmsRequest } from '../../models/verify-sms-request.model';

@Injectable({ providedIn: 'root' })
export class UserAuthApiService {
    private readonly http = inject(HttpClient);

    login(request: LoginRequest): Observable<LoginResponse> {
        return this.http
            .post<ApiResponse<LoginResponse>>(
                `${environment.apiBaseUrl}/users/me/auth/login`,
                request,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to login');
                    }
                    return resp.result ?? ({} as LoginResponse);
                })
            );
    }

    verifySms(request: VerifySmsRequest): Observable<LoginResponse> {
        return this.http
            .post<ApiResponse<LoginResponse>>(
                `${environment.apiBaseUrl}/users/me/auth/verify-sms`,
                request,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to verify SMS');
                    }
                    return resp.result ?? ({} as LoginResponse);
                })
            );
    }

    resendOtp(challengeId: string): Observable<void> {
        return this.http
            .post<ApiResponse<unknown>>(
                `${environment.apiBaseUrl}/users/me/auth/resend-otp`,
                { challengeId },
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to resend OTP');
                    }
                    return undefined;
                })
            );
    }

    logout(): Observable<void> {
        return this.http
            .post<ApiResponse<unknown>>(
                `${environment.apiBaseUrl}/users/me/auth/logout`,
                null,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to logout');
                    }
                    return undefined;
                })
            );
    }

    checkAuth(): Observable<boolean> {
        return this.http
            .get<ApiResponse<{ authenticated: boolean }>>(
                `${environment.apiBaseUrl}/users/me/auth/check`,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        return false;
                    }
                    return resp.result?.authenticated ?? false;
                })
            );
    }

    keepAlive(): Observable<void> {
        return this.http
            .post<ApiResponse<unknown>>(
                `${environment.apiBaseUrl}/users/me/auth/keep-alive`,
                null,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to keep alive');
                    }
                    return undefined;
                })
            );
    }
}
